/**
 * Centro de mejora: calificar la limpieza, reportar una incidencia, sugerir un
 * producto.
 *
 * Rediseñado a pedido del dueño (29-ago-2026): «estas interfaces deberían ser
 * más de la marca, más playful… y menos abrumadoras». El patrón viene de las
 * apps que la gente ya usa todos los días (Uber, Airbnb, Rappi): primero se
 * TOCA —estrellas grandes, chips de qué pasó— y escribir es opcional y viene
 * plegado. Un formulario con dos campos de texto en blanco es un examen; un
 * chip es una conversación.
 */

import { useRef, useState, type KeyboardEvent } from 'react';
import {
  dateLabel, INCIDENT_STATUS_LABEL, INCIDENT_STATUS_TONE, type IncidentStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Badge, Button, Empty, Field, Modal, Note } from '@/components/ui';
import { Icon, type IconName } from '@/components/common/NavIcons';

interface Incident {
  id: string; title: string; description: string | null; category: string;
  status: IncidentStatus; resolution: string | null; created_at: string; unit_code: string;
}

type Mode = 'rate' | 'incident' | 'product';

const ENTRADAS: { mode: Mode; icon: IconName; titulo: string; sub: string }[] = [
  { mode: 'rate', icon: 'mejora', titulo: 'Calificar la limpieza', sub: 'De 1 a 5 estrellas' },
  { mode: 'incident', icon: 'incidencias', titulo: 'Reportar una incidencia', sub: 'Contanos qué pasó' },
  { mode: 'product', icon: 'inventario', titulo: 'Sugerir un producto', sub: 'Algo que te gustaría que usemos' },
];

export function PortalImprove() {
  const home = useApi<{ units: { id: string; unit_code: string }[] }>('/portal/home');
  const incidents = useApi<{ incidents: Incident[] }>('/portal/incidents');
  const [mode, setMode] = useState<Mode | null>(null);

  const unitId = home.data?.units[0]?.id;

  return (
    <>
      <section className="msec">
        <div className="msec__title">¿Cómo podemos mejorar?</div>
        {/* En escritorio, tres tarjetas una al lado de la otra (dos si la
            ventana es angosta); en el teléfono la rejilla no existe. */}
        <div className="mejora-grid">
          {ENTRADAS.map((e) => (
            <button key={e.mode} type="button" className="mejora-card" onClick={() => setMode(e.mode)}>
              <span className="mejora-card__ico"><Icon name={e.icon} size={20} /></span>
              <span className="mejora-card__txt">
                <strong>{e.titulo}</strong>
                <span>{e.sub}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="msec">
        <div className="msec__title">Mis reportes</div>
        <Async query={incidents} empty={(d) => d.incidents.length === 0 && (
          <Empty icon="mejora" title="Todavía no hay reportes" />
        )}>
          {(data) => (
            <div className="portal-grid2">
              {data.incidents.map((i) => (
                <div key={i.id} className="mcard">
                  <div className="mrow" style={{ paddingTop: 0 }}>
                    <strong>{i.title}</strong>
                    <Badge tone={INCIDENT_STATUS_TONE[i.status]}>{INCIDENT_STATUS_LABEL[i.status]}</Badge>
                  </div>
                  {i.description && (
                    <p className="small muted" style={{ margin: '6px 0' }}>{i.description}</p>
                  )}
                  {i.resolution && (
                    <Note tone="ok">{i.resolution}</Note>
                  )}
                  <div className="mrow" style={{ borderBottom: 0, paddingBottom: 0 }}>
                    <span className="mrow__l">{dateLabel(i.created_at.slice(0, 10))}</span>
                    <span className="mrow__r small muted">{i.unit_code}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Async>
      </section>

      {mode && unitId && <ImproveModal mode={mode} unitId={unitId} onClose={() => setMode(null)} />}
      {mode && !unitId && (
        <div className="msec">
          <Note tone="warn">Tu cuenta todavía no está asociada a un consultorio.</Note>
        </div>
      )}
    </>
  );
}

/** La reacción que acompaña a cada cantidad de estrellas: el toque playful
 *  que convierte la calificación en una respuesta, no en un número. */
const REACCION: Record<number, string> = {
  1: 'Muy mal — lo vamos a arreglar',
  2: 'Mal — gracias por decirlo',
  3: 'Regular',
  4: 'Bien',
  5: '¡Excelente!',
};

/**
 * Qué pasó, para elegir en vez de redactar. Cada chip mapea a la categoría
 * formal del caso; el texto libre queda como detalle opcional. Las opciones
 * salen de lo que de verdad pasa en un servicio de limpieza (y de cómo lo
 * preguntan las apps de servicios): calidad, ausencia, daño, y el resto.
 */
const QUE_PASO: { titulo: string; categoria: string }[] = [
  { titulo: 'Quedó mal limpiado', categoria: 'CLEANING_QUALITY' },
  { titulo: 'No llegaron a limpiar', categoria: 'SERVICE_FAILURE' },
  { titulo: 'Algo se dañó', categoria: 'PROPERTY_DAMAGE' },
  { titulo: 'Otra cosa', categoria: 'OTHER' },
];

const ESTRELLAS = [1, 2, 3, 4, 5];

function ImproveModal({ mode, unitId, onClose }: {
  mode: Mode; unitId: string; onClose: () => void;
}) {
  /* Las estrellas ARRANCAN VACÍAS: precargar 5 regala la nota y sesga el
     dato. Elegir es un toque; el default no debe opinar. */
  const [satisfaction, setSatisfaction] = useState(0);
  const [quePaso, setQuePaso] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [comentarioAbierto, setComentarioAbierto] = useState(false);
  const estrellas = useRef<(HTMLButtonElement | null)[]>([]);

  /* Un radiogroup de verdad: un solo tabulador entra al grupo (roving
     tabindex) y las flechas ← → (y ↑ ↓, Inicio, Fin) mueven la elección y el
     foco juntos, como un radio nativo. */
  function teclaEstrella(e: KeyboardEvent<HTMLButtonElement>, n: number) {
    let destino: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') destino = Math.min(5, n + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') destino = Math.max(1, n - 1);
    else if (e.key === 'Home') destino = 1;
    else if (e.key === 'End') destino = 5;
    if (destino === null) return;
    e.preventDefault();
    setSatisfaction(destino);
    estrellas.current[destino - 1]?.focus();
  }
  /* Sin elección, el foco entra por la primera; con elección, por la elegida. */
  const estrellaTabulable = satisfaction === 0 ? 1 : satisfaction;

  const mutation = useApiMutation(
    () => {
      if (mode === 'rate') {
        return api.post('/portal/feedback', {
          service_unit_id: unitId, satisfaction, comment: text || undefined,
        });
      }
      if (mode === 'incident') {
        const elegido = QUE_PASO[quePaso!]!;
        return api.post('/portal/incidents', {
          service_unit_id: unitId, title: elegido.titulo, detail: text || undefined,
          category: elegido.categoria, severity: 'NORMAL',
        });
      }
      return api.post('/portal/product-requests', {
        service_unit_id: unitId, product_name: title, description: text || undefined,
      });
    },
    ['/portal/incidents'],
  );

  const titles = {
    rate: 'Calificar la limpieza',
    incident: 'Reportar una incidencia',
    product: 'Sugerir un producto',
  };

  const canSubmit = mode === 'rate'
    ? satisfaction > 0
    : mode === 'incident'
      // «Otra cosa» sin contar qué cosa no le sirve a nadie: ahí el detalle
      // sí es obligatorio. En las demás, el chip ya lo dice.
      ? quePaso !== null && (QUE_PASO[quePaso]!.categoria !== 'OTHER' || text.trim().length >= 3)
      : title.trim().length >= 3;

  /* El `Modal` del sistema (Escape, foco, impresión) en vez de una copia a
     mano del backdrop: el contenido y los botones son los mismos de antes. */
  return (
    <Modal
      title={titles[mode]}
      onClose={onClose}
      footer={mutation.isSuccess ? (
        <Button variant="primary" block onClick={onClose}>Cerrar</Button>
      ) : (
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess ? (
        <Note tone="ok">
          {mode === 'rate'
            ? '¡Gracias! Tu opinión llega directo al equipo.'
            : mode === 'incident'
              ? 'Recibimos tu reporte. Te vamos a contar cómo se resolvió.'
              : 'Gracias por la sugerencia. Un administrador la va a revisar.'}
        </Note>
      ) : (
        <>
          {mode === 'rate' && (
            <>
              <div className="rate-stars" role="radiogroup" aria-label="Calificación de 1 a 5">
                {ESTRELLAS.map((n) => (
                  <button
                    key={n}
                    ref={(el) => { estrellas.current[n - 1] = el; }}
                    type="button"
                    role="radio"
                    aria-checked={satisfaction === n}
                    aria-label={`${n} de 5`}
                    tabIndex={n === estrellaTabulable ? 0 : -1}
                    className={`rate-stars__star${n <= satisfaction ? ' is-on' : ''}`}
                    onClick={() => setSatisfaction(n)}
                    onKeyDown={(e) => teclaEstrella(e, n)}
                  >
                    {/* La estrella de la familia, no el glifo «★» (que en iOS
                        salía como emoji dorado). Se rellena en `.is-on`. */}
                    <Icon name="estrella" size={34} />
                  </button>
                ))}
              </div>
              <p className="rate-stars__reaccion" aria-live="polite">
                {satisfaction > 0 ? REACCION[satisfaction] : '¿Qué tal estuvo la limpieza?'}
              </p>
              {/* El comentario existe pero no estorba: plegado hasta que
                  alguien de verdad quiera escribir. Botón propio, no el
                  `Button ghost` gris — con las estrellas centradas arriba,
                  un gris apagado se perdía; en el azul de marca, a la
                  izquierda, se lee como una acción de verdad (dueño,
                  2-sep-2026). */}
              {!comentarioAbierto ? (
                <button type="button" className="rate-stars__comentario-btn"
                  onClick={() => setComentarioAbierto(true)}>
                  <Icon name="documento" size={16} />
                  Agregar un comentario
                </button>
              ) : (
                <Field label="Comentario">
                  <textarea className="input" rows={3} value={text} autoFocus
                    onChange={(e) => setText(e.target.value)} />
                </Field>
              )}
            </>
          )}

          {mode === 'incident' && (
            <>
              <p className="small muted mb-2">¿Qué pasó? Tocá lo que corresponda.</p>
              <div className="chip-opciones">
                {QUE_PASO.map((op, i) => (
                  <button
                    key={op.titulo}
                    type="button"
                    aria-pressed={quePaso === i}
                    className={`chip-opcion${quePaso === i ? ' is-on' : ''}`}
                    onClick={() => setQuePaso(i)}
                  >
                    {op.titulo}
                  </button>
                ))}
              </div>
              {quePaso !== null && (
                <Field
                  label="Contanos más"
                  hint={QUE_PASO[quePaso]!.categoria === 'OTHER'
                    ? 'Acá sí necesitamos que nos cuentes qué pasó.'
                    : 'Opcional — lo que ayude a resolverlo.'}
                >
                  <textarea className="input" rows={3} value={text}
                    onChange={(e) => setText(e.target.value)} />
                </Field>
              )}
            </>
          )}

          {mode === 'product' && (
            <>
              <Field label="¿Qué producto?" required>
                <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
              </Field>
              <Field label="Para qué serviría">
                <textarea className="input" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
              </Field>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
